import { classNames, isAdjustedOdds, isStandard } from '@library/utils';
import { forwardRef, useEffect, useState } from 'react';
import { NumberInput } from '@library/components/inputs';
import type { OddsType } from '@library/types/projection';
import { OddsTypeIndicator } from '@library/components/OddsTypeIndicator';
import { PromoIndicator } from '@library/components/PromoIndicator';
import type { Ref } from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import { formatProjectionValueNumberString } from '@bo-utils/projectionDisplay';

export interface ProjectionInputProps {
  readonly className?: string;
  readonly disabled?: boolean;
  readonly id: string;
  readonly isCommunityPlay?: boolean;
  readonly isPromo?: boolean;
  readonly isExistingProjection?: boolean;
  readonly oddsType?: OddsType;
  readonly onValueChange: (value: string) => void;
  readonly optIn?: boolean;
  readonly simsProbabilityOfMore?: number;
  readonly testId?: string;
  readonly value: number | '';
}

const CustomProjectionInput = (
  {
    className = '',
    disabled = false,
    id,
    isCommunityPlay = false,
    isPromo = false,
    isExistingProjection = false,
    oddsType = 'standard',
    onValueChange,
    optIn = false,
    simsProbabilityOfMore = 0.5,
    testId = 'projection-input',
    value
  }: ProjectionInputProps,
  ref: Ref<HTMLInputElement>
) => {
  const [projectionValue, setProjectionValue] = useState<string>(
    formatProjectionValueNumberString(String(value))
  );

  useEffect(() => {
    setProjectionValue(String(value));
  }, [value]);

  const handleOnValueChange = (newValue: string) => {
    setProjectionValue(newValue);
    onValueChange(newValue);
  };

  const handleOnBlur = () => {
    const formattedValue = formatProjectionValueNumberString(projectionValue);

    setProjectionValue(formattedValue);
  };

  const showOdds = isExistingProjection
    ? isAdjustedOdds(simsProbabilityOfMore) && !isStandard(oddsType)
    : isAdjustedOdds(simsProbabilityOfMore) || !isStandard(oddsType);
  const suffixNumber = [showOdds, isPromo].filter(Boolean).length;

  return (
    <div className="relative flex">
      <NumberInput
        className={classNames(
          'font-heebo text-soClean-100 disabled:text-soClean-160 h-8 w-14 bg-transparent px-0 text-center text-sm font-medium leading-5 tracking-tight',
          className,
          suffixNumber > 0 ? 'z-10 !w-20 pr-6' : '',
          suffixNumber === 2 ? '!w-24 pr-11' : ''
        )}
        disabled={disabled}
        id={id}
        onBlur={handleOnBlur}
        onValueChange={handleOnValueChange}
        placeholder="-"
        ref={ref}
        value={projectionValue}
        data-testid={testId}
      />

      <div className="absolute right-2 top-0 ml-auto flex h-8 flex-row items-center">
        <div className="border-soFresh-140 flex items-center border-l-2 pl-2">
          {isPromo && <PromoIndicator className="size-4" optIn={optIn} />}

          {isCommunityPlay && (
            <UsersIcon
              title="Community Play"
              className="text-msJackson ml-1 size-4"
            />
          )}

          {showOdds && simsProbabilityOfMore < 0.47 ? (
            <OddsTypeIndicator className="size-4" oddsType="demon" />
          ) : (
            simsProbabilityOfMore > 0.53 && (
              <OddsTypeIndicator className="size-4" oddsType="goblin" />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export const ProjectionInput = forwardRef(CustomProjectionInput);
